import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { PageHeader, Panel, Banner, Button, Modal, DefinitionList } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { LeadStatus, TierBadge, QuickAnswer, TIMING, type Lead } from './ClientLeads'
import { Term } from '../../components/Term'
import type { Summary } from './ClientOverview'
import { disputeLabel } from './disputes'
import { WeeklyLeadBanner, tierOf, unbilled, isWeekly } from './weekly'

const URGENCY: Record<string, string> = { ...TIMING, unknown: 'Not said' }

export default function ClientLead() {
  const { id = '' } = useParams()
  const lead = useApi<{ lead: Lead }>(`/api/client/leads/${encodeURIComponent(id)}`)
  const summary = useApi<Summary>('/api/client/summary')
  const [zoom, setZoom] = useState<string | null>(null)

  if (lead.error) {
    return (
      <>
        <Link className="btn btn-ghost" to="/portal/leads"><Icon of={ArrowLeft} /> All leads</Link>
        <div style={{ marginTop: 'var(--s-6)' }}><ErrorBlock error={lead.error} /></div>
      </>
    )
  }
  if (!lead.data) return <LoadingBlock />

  const l = lead.data.lead
  const tz = summary.data?.org.timezone
  const orgName = summary.data?.org.name || 'Handpicked'
  const when = (d: string | null) => d
    ? new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(d))
    : ''
  const refunded = Boolean(l.refunded_at) || l.dispute_status === 'approved'
  const image = `/api/client/leads/${encodeURIComponent(l.id)}/image`
  const where = l.area_name || l.area_text


  const shots = [
    { id: 'first', when: l.responded_at, image: l.has_image ? image : null, note: l.reply_note, first: true },
    ...l.conversation.map((c) => ({ id: c.id, when: c.created_at, image: c.has_image ? `/api/client/leads/${encodeURIComponent(l.id)}/conversation/${c.id}` : null, note: c.note, first: false })),
  ]
  const cost = refunded ? 0 : (l.billed_cents || 0)
  const savedHere = !unbilled(l) && !refunded && l.list_cents != null ? l.list_cents - (l.billed_cents || 0) : 0
  const tier = tierOf(l)

  return (
    <>
      <div style={{ marginBottom: 'var(--s-4)' }}>
        <Link className="btn btn-ghost" to="/portal/leads"><Icon of={ArrowLeft} /> All leads</Link>
      </div>
      <PageHeader title={l.summary || 'Asked for a recommendation'} subtitle={[when(l.sent_at), where, l.group_name].filter(Boolean).join(' · ')}
        actions={<div className="row-tight" style={{ alignItems: 'center' }}><TierBadge name={tier.name} tierKey={tier.key} large /><LeadStatus lead={l} /></div>} />

      {isWeekly(l) && !l.billed_at && !refunded && <div style={{ marginBottom: 'var(--s-6)' }}><WeeklyLeadBanner lead={l} when={when} onDone={lead.reload} /></div>}
      {!l.grading && !refunded && <div className="panel" style={{ marginBottom: 'var(--s-6)', padding: 'var(--s-5) var(--s-6)' }}><QuickAnswer lead={l} /></div>}
      {l.grading && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="neutral" title="Being graded">The rep is still talking with them. The lead is graded on the whole conversation{l.grade_due_at ? ` by ${when(l.grade_due_at)} at the latest` : ''}, and you are charged then, at that tier.</Banner></div>}
      {l.dispute_status && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Panel>
            <div className="panel-header">
              <h2 className="panel-title">Your dispute</h2>
              <span className={`pill ${l.dispute_status === 'open' ? 'is-warn' : l.dispute_status === 'approved' ? 'is-good' : ''}`.trim()}>
                {l.dispute_status === 'open' ? 'Being reviewed' : l.dispute_status === 'approved' ? 'Refunded' : 'Declined'}
              </span>
            </div>
            <DefinitionList items={[
              { label: 'What was wrong', value: disputeLabel(l.dispute_category) },
              { label: 'You said', value: l.dispute_reason || '' },
              ...(l.dispute_note ? [{ label: `${orgName} said`, value: l.dispute_note }] : []),
            ]} />
            {l.has_dispute_image && (
              <button type="button" className="timeline-shot" style={{ padding: 0, background: 'none', cursor: 'zoom-in', marginTop: 'var(--s-4)', maxWidth: 220 }}
                      onClick={() => setZoom(`/api/client/leads/${encodeURIComponent(l.id)}/dispute-image`)} aria-label="Open your screenshot full size">
                <img src={`/api/client/leads/${encodeURIComponent(l.id)}/dispute-image`} alt="The screenshot you sent" loading="lazy" />
              </button>
            )}
          </Panel>
        </div>
      )}

      <div className="wide-narrow">
        <div className="stack">
          <Panel>
            <div className="panel-header"><h2 className="panel-title">The request</h2>{l.post_url && <a href={l.post_url} target="_blank" rel="noopener noreferrer">Open on Facebook <Icon of={ExternalLink} size={14} /></a>}</div>
            {l.post_text ? <p className="timeline-note" style={{ marginTop: 0 }}>{l.post_text}</p> : null}
            <DefinitionList items={[
              ...(l.person_name ? [{ label: 'Who', value: <>{l.person_name}{l.person_lives_in ? `, lives in ${l.person_lives_in}` : ''}{l.profile_url && <> · <a href={l.profile_url} target="_blank" rel="noopener noreferrer">Profile <Icon of={ExternalLink} size={13} /></a></>}</> }] : []),
              ...(l.person_phone || l.person_email || l.phone_notice ? [{ label: 'Contact they shared', value: <>{[l.person_phone, l.person_email].filter(Boolean).join(' · ')}{l.phone_notice && <div className={l.phone_removed ? 'muted' : 'post-cut'}>{l.phone_notice}</div>}</> }] : []),
              { label: 'What they need', value: l.summary || 'A recommendation' },
              ...(l.posted_at_text ? [{ label: 'Posted', value: l.posted_at_text }] : []),
              { label: 'When', value: URGENCY[l.urgency || 'unknown'] || 'Not said' },
              ...(where ? [{ label: 'Area', value: where }] : []),
              ...(l.category_name ? [{ label: 'Service', value: l.category_name }] : []),
              ...(l.job_type_name ? [{ label: 'Job', value: l.job_type_name }] : []),
              ...(l.lane_name ? [{ label: 'Job tier', value: `${l.lane_name}${l.lane_urgent ? ', one up because they need it now' : ''}` }] : []),
              ...(l.group_name ? [{ label: 'Facebook group', value: l.group_name }] : []),
            ]} />
          </Panel>

          <Panel>
            <div className="panel-header"><h2 className="panel-title">The conversation</h2><span className="muted">{shots.length} message{shots.length === 1 ? '' : 's'}{l.rep_first_name ? ` from ${l.rep_first_name}` : ''}</span></div>
            <ol className="timeline">
              {shots.map((m) => (
                <li key={m.id}>
                  <span className={m.first ? 'timeline-dot is-first' : 'timeline-dot'} aria-hidden="true" />
                  <div>
                    <div className="timeline-when">{m.first ? 'Recommended you' : 'Later'}{m.when ? ` · ${when(m.when)}` : ''}</div>
                    {m.image && (
                      <button type="button" className="timeline-shot" onClick={() => setZoom(m.image)} style={{ padding: 0, background: 'none', cursor: 'zoom-in' }} aria-label="Open the screenshot full size">
                        <img src={m.image} alt={m.first ? 'The reply recommending you' : 'A screenshot from the conversation'} loading="lazy" />
                      </button>
                    )}
                    {m.note && <p className="timeline-note">{m.note}</p>}
                  </div>
                </li>
              ))}
            </ol>
            {l.details && <div style={{ marginTop: 'var(--s-6)' }}><span className="hero-kicker">What the rep learned</span><p className="timeline-note">{l.details}</p></div>}
          </Panel>
        </div>

        <div className="stack">
          <Panel>
            <div className="panel-header"><h2 className="panel-title"><Term tip="tier">Why this tier</Term></h2><TierBadge name={tier.name} tierKey={tier.key} /></div>
            <p style={{ margin: 0 }}>{l.grading ? 'Graded when the conversation is done.' : (l.tier_reason || 'Graded on the conversation.')}</p>
          </Panel>

          <Panel>
            <div className="panel-header"><h2 className="panel-title">What it cost</h2></div>
            {l.grading ? <p style={{ margin: 0 }}>Not charged yet. You are charged at the tier it is graded, and can dispute it after that.</p>
              : isWeekly(l) && !l.billed_at ? <p style={{ margin: 0 }}>Not charged yet. Claimed leads go on the weekly invoice after the Sunday lock: your tab credit pays first, at the tab price, and the rest is charged to your card at the full price.</p> : (
              <div className="cost-rows">
                {l.list_cents != null && <div className="cost-row"><span>{l.tier_name ? `${l.tier_name} lead` : 'Lead'}</span><span className="num">{money(l.list_cents)}</span></div>}
                {l.prepaid_credit_cents > 0 && <div className="cost-row"><span className="muted">{isWeekly(l) ? 'From your tab' : 'From your balance'}</span><span className="num muted">{money(l.prepaid_credit_cents)} credit</span></div>}
                {l.overage_cents > 0 && <div className="cost-row"><span className="muted">{isWeekly(l) ? 'Charged to your card, at full price' : 'On your bill, at full price'}</span><span className="num muted">{money(l.overage_cents)}</span></div>}
                <div className="cost-row is-total"><span>You paid</span><span className="num">{money(cost)}</span></div>
                {savedHere > 0 && <span className="savings">{money(savedHere)} saved by {isWeekly(l) ? 'your tab' : 'prepaying'}</span>}
                {refunded && <span className="savings">Refunded, so this lead cost nothing</span>}
              </div>
            )}
          </Panel>



          {l.can_dispute && (
            <Panel>
              <div className="panel-header"><h2 className="panel-title">Something wrong with this lead?</h2></div>
              <p style={{ marginTop: 0 }}>
                You can dispute it until {when(l.dispute_until)}.
              </p>
              <Link className="btn btn-secondary" to={`/portal/leads/${encodeURIComponent(l.id)}/dispute`}>Dispute this lead</Link>
            </Panel>
          )}
        </div>
      </div>

      {zoom && (
        <Modal title="Screenshot" onClose={() => setZoom(null)} footer={<Button variant="secondary" onClick={() => setZoom(null)}>Close</Button>}>
          <img className="rec-shot-full" src={zoom} alt="The screenshot, full size" style={{ maxWidth: '100%' }} />
        </Modal>
      )}
    </>
  )
}
